/**
 * KnativeWorkflowsContent - Knative CD Pipelines tab 内容
 *
 * 列表 / 历史 / Stage 展开 / 日志查看 / 配置查看的渲染与交互**完全照搬 k8s 模式**
 * （SystemWorkflowsContent），仅把数据源与执行入口换成 knative：
 *   - 数据/触发：useKnativePipelineActions（runs 来自 Tekton Results，执行走 trigger-knative）
 *   - 历史详情/日志/配置：从 tekton-control / tekton-pipelines 实时取（与 k8s 一致）
 *
 * k8s 的 SystemWorkflowsContent 一行未动，互不影响。
 */
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Typography,
  Button,
  Box,
  makeStyles,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  Tooltip,
  Divider,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Popover,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import RefreshIcon from '@material-ui/icons/Refresh';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import CloseIcon from '@material-ui/icons/Close';
import DescriptionIcon from '@material-ui/icons/Description';
import SettingsIcon from '@material-ui/icons/Settings';
import CodeIcon from '@material-ui/icons/Code';
import AccountTreeIcon from '@material-ui/icons/AccountTree';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CancelIcon from '@material-ui/icons/Cancel';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked';
import { useApi } from '@backstage/core-plugin-api';
import { kubernetesApiRef } from '@backstage/plugin-kubernetes-react';
import { useKnativePipelineActions } from './useKnativePipelineActions';

// ── Types ──

interface TektonPipelineRun {
  metadata: {
    name: string;
    namespace: string;
    creationTimestamp: string;
    labels?: Record<string, string>;
    annotations?: Record<string, string>;
  };
  spec?: {
    pipelineRef?: { name: string };
    params?: Array<{ name: string; value: string | string[] }>;
  };
  status?: {
    conditions?: Array<{
      type: string;
      status: string;
      reason: string;
      message?: string;
    }>;
    startTime?: string;
    completionTime?: string;
  };
}

// ── Pipeline 元数据（knative） ──

const PIPELINE_META: Record<string, {
  icon: string;
  tags: string[];
  description: string;
}> = {
  'backstage-knative-release': {
    icon: '🚀',
    tags: ['Knative', '发版'],
    description: '更新 Knative Service 镜像，产生新的 Revision',
  },
  'backstage-knative-traffic-split': {
    icon: '🔀',
    tags: ['Knative', '切流'],
    description: '调整 Revision 之间的流量分配',
  },
};

const PIPELINE_ORDER = [
  'backstage-knative-release',
  'backstage-knative-traffic-split',
];

// 列表 / 标题展示名（对齐用户要求的文案）
const PIPELINE_DISPLAY: Record<string, string> = {
  'backstage-knative-release': '新建版本',
  'backstage-knative-traffic-split': '切换流量',
};

const displayName = (pipelineName: string): string =>
  PIPELINE_DISPLAY[pipelineName] || pipelineName.replace('backstage-knative-', '');

// ── Helpers（与 k8s 模式一致） ──

function getRunStatus(run: TektonPipelineRun): string {
  const conditions = run.status?.conditions || [];
  const succeeded = conditions.find(c => c.type === 'Succeeded');
  if (!succeeded) {
    if (run.status?.startTime) return 'Running';
    return 'Pending';
  }
  if (succeeded.status === 'True') return 'Succeeded';
  if (succeeded.status === 'False') {
    if (succeeded.reason === 'Cancelled' || succeeded.reason === 'PipelineRunCancelled')
      return 'Cancelled';
    return 'Failed';
  }
  return 'Running';
}

function getRunErrorMessage(run: TektonPipelineRun): string | undefined {
  if (!run.status?.conditions) return undefined;
  const succeeded = run.status.conditions.find((c: any) => c.type === 'Succeeded');
  if (succeeded && succeeded.status === 'False') {
    return succeeded.message || succeeded.reason;
  }
  return undefined;
}

function getStatusColor(status: string): string {
  switch (status) {
    case 'Succeeded': return '#4caf50';
    case 'Failed': return '#f44336';
    case 'Running': return '#2196f3';
    case 'Pending': return '#ff9800';
    case 'Cancelled': return '#9e9e9e';
    default: return '#757575';
  }
}

function durationSeconds(start?: string, end?: string): number {
  if (!start) return 0;
  const s = new Date(start).getTime();
  const e = end ? new Date(end).getTime() : Date.now();
  return Math.floor((e - s) / 1000);
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m < 60) return `${m}m ${s}s`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hour = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${month}-${day} ${hour}:${min}`;
}

// 目标集群 / 命名空间：knative run 的 cluster-name / namespace 在 params 中
function getRunTarget(run: TektonPipelineRun): { cluster: string; namespace: string } {
  const params = run.spec?.params || [];
  const c = params.find(p => p.name === 'cluster-name');
  const n = params.find(p => p.name === 'namespace');
  return {
    cluster: (typeof c?.value === 'string' ? c.value : '') || '',
    namespace: (typeof n?.value === 'string' ? n.value : '') || '',
  };
}

// triggered-by：knative run 写在 annotations（k8s 写在 labels），两者兼容
function getTriggeredBy(run: TektonPipelineRun): string {
  return (
    run.metadata.annotations?.['backstage.io/triggered-by'] ||
    run.metadata.labels?.['backstage.io/triggered-by'] ||
    ''
  );
}

// ── Styles（照搬 k8s 模式） ──

const useStyles = makeStyles(theme => ({
  root: {
    padding: 0,
    overflowX: 'auto',
  },
  filterBar: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap' as const,
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(3),
    padding: theme.spacing(1.5, 2),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',
    border: `1px solid ${theme.palette.divider}`,
  },
  pipelineCard: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(2, 2.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': { borderBottom: 'none' },
    '&:hover': { backgroundColor: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.015)' },
    transition: 'background-color 0.15s',
    gap: theme.spacing(3),
  },
  pipelineInfo: {
    flex: '0 0 200px',
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column' as const,
    justifyContent: 'center',
  },
  pipelineName: {
    fontWeight: 600,
    fontSize: '0.95rem',
    color: theme.palette.primary.main,
    cursor: 'pointer',
    '&:hover': { textDecoration: 'underline' },
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  tag: {
    height: 20,
    fontSize: '0.65rem',
    fontWeight: 500,
    borderRadius: 4,
  },
  miniChart: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'flex-end',
    gap: 2,
    height: 36,
    flex: '0 0 160px',
    padding: '0 8px',
  },
  miniBar: {
    flex: 1,
    minWidth: 6,
    maxWidth: 12,
    borderRadius: 2,
    transition: 'opacity 0.15s',
    cursor: 'pointer',
    '&:hover': { opacity: 0.8 },
  },
  statsSection: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    flex: 1,
    justifyContent: 'space-evenly',
  },
  statItem: {
    textAlign: 'center' as const,
    minWidth: 80,
  },
  statValue: {
    fontWeight: 600,
    fontSize: '0.95rem',
    lineHeight: 1.2,
  },
  statLabel: {
    fontSize: '0.7rem',
    color: theme.palette.text.secondary,
  },
  executeBtn: {
    flexShrink: 0,
  },
  historyTable: {
    '& th': {
      fontWeight: 600,
      fontSize: '0.8rem',
      color: theme.palette.text.secondary,
      whiteSpace: 'nowrap',
      borderBottom: `2px solid ${theme.palette.divider}`,
    },
    '& td': {
      fontSize: '0.85rem',
      padding: '10px 16px',
    },
  },
  historyRunId: {
    color: theme.palette.primary.main,
    fontWeight: 500,
    fontFamily: 'monospace',
  },
  historyStatusChip: {
    fontWeight: 500,
    fontSize: '0.7rem',
    height: 22,
  },
  historyDialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  expandIcon: {
    fontSize: 18,
    color: theme.palette.text.secondary,
    cursor: 'pointer',
    transition: 'transform 0.2s ease',
  },
  expandedRow: {
    backgroundColor: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.015)',
  },
  stageRow: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1, 2),
    gap: theme.spacing(2),
    borderTop: `1px dashed ${theme.palette.divider}`,
    '&:first-child': { borderTop: 'none' },
  },
  stageName: {
    fontWeight: 500,
    fontSize: '0.85rem',
    minWidth: 120,
  },
  stageStatusChip: {
    fontWeight: 500,
    fontSize: '0.65rem',
    height: 20,
  },
  stageDuration: {
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
  logButton: {
    fontSize: '0.7rem',
    textTransform: 'none' as const,
    minWidth: 0,
    padding: theme.spacing(0.25, 1),
  },
  configBody: {
    display: 'flex',
    flex: 1,
    overflow: 'hidden',
  },
  configCanvas: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'auto',
    padding: theme.spacing(4),
    backgroundColor: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.02)' : '#f7f8fa',
  },
  configSidebar: {
    width: 280,
    flexShrink: 0,
    borderLeft: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(2),
    overflowY: 'auto' as const,
  },
  sidebarSection: {
    marginBottom: theme.spacing(2.5),
  },
  sidebarLabel: {
    fontSize: '0.7rem',
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.08em',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
  sidebarItem: {
    fontSize: '0.78rem',
    padding: theme.spacing(0.5, 0),
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 2,
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': { borderBottom: 'none' },
  },
  flowContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 0,
    position: 'relative' as const,
    padding: theme.spacing(2, 0),
  },
  flowStartEndLabel: {
    fontSize: '0.85rem',
    fontWeight: 600,
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap' as const,
  },
  flowDot: {
    width: 10,
    height: 10,
    borderRadius: '50%',
    border: `2px solid ${theme.palette.primary.main}`,
    backgroundColor: theme.palette.background.paper,
    flexShrink: 0,
  },
  flowLine: {
    height: 2,
    backgroundColor: theme.palette.primary.main,
    opacity: 0.5,
    flexShrink: 0,
  },
  flowAddStageBtn: {
    fontSize: '0.7rem',
    color: theme.palette.text.disabled,
    cursor: 'not-allowed',
    whiteSpace: 'nowrap' as const,
    padding: '2px 6px',
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: 4,
    backgroundColor: 'transparent',
    userSelect: 'none' as const,
  },
  flowStageColumn: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'stretch',
    flexShrink: 0,
  },
  flowStageHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
    paddingLeft: theme.spacing(0.5),
  },
  flowStageName: {
    fontSize: '0.8rem',
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  flowStageGear: {
    fontSize: 14,
    color: theme.palette.text.disabled,
    cursor: 'not-allowed',
  },
  flowStageBox: {
    border: `1.5px dashed ${theme.palette.divider}`,
    borderRadius: 6,
    padding: theme.spacing(1.5),
    minWidth: 260,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: theme.spacing(1),
  },
  flowTaskBox: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    padding: theme.spacing(1, 3),
    backgroundColor: theme.palette.background.paper,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  flowTaskName: {
    fontSize: '0.82rem',
    fontWeight: 500,
    color: theme.palette.text.primary,
    textAlign: 'center' as const,
  },
  flowAddTaskBtn: {
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: 4,
    padding: theme.spacing(0.5, 1),
    fontSize: '0.72rem',
    color: theme.palette.text.disabled,
    cursor: 'not-allowed',
    textAlign: 'center' as const,
    userSelect: 'none' as const,
  },
  viewToggle: {
    display: 'flex',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 6,
    overflow: 'hidden',
  },
  viewToggleBtn: {
    padding: theme.spacing(0.4, 1.5),
    fontSize: '0.75rem',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    border: 'none',
    outline: 'none',
    transition: 'background-color 0.15s',
  },
}));

// ── Component ──

export const KnativeWorkflowsContent: React.FC = () => {
  const classes = useStyles();
  const kubernetesApi = useApi(kubernetesApiRef);

  const {
    pipelines,
    pipelineRuns,
    pipelinesLoading: loading,
    refresh,
    executingPipelines,
    hasKnativeConfig,
    openReleaseDialog,
    openTrafficDialog,
    tektonClusterName: tektonCluster,
    tektonNamespace,
    dialogs,
  } = useKnativePipelineActions();

  const [historyDialog, setHistoryDialog] = useState<{ open: boolean; pipelineName: string }>({ open: false, pipelineName: '' });

  // 展开的 run 详情（支持多行展开）
  const [expandedRuns, setExpandedRuns] = useState<Set<string>>(new Set());
  const [runDetails, setRunDetails] = useState<Record<string, any>>({});
  const [detailsLoading, setDetailsLoading] = useState<Record<string, boolean>>({});

  // 日志弹窗
  const [logDialog, setLogDialog] = useState<{ open: boolean; taskName: string; log: string; loading: boolean; podName: string; taskStatus: string }>({
    open: false, taskName: '', log: '', loading: false, podName: '', taskStatus: '',
  });

  // Pipeline 配置查看
  const [configDialog, setConfigDialog] = useState<{
    open: boolean;
    pipelineName: string;
    pipeline: any | null;
    loading: boolean;
    view: 'graph' | 'yaml';
  }>({ open: false, pipelineName: '', pipeline: null, loading: false, view: 'graph' });

  const [taskPopover, setTaskPopover] = useState<{ anchorEl: HTMLElement | null; task: any | null }>({ anchorEl: null, task: null });

  const openConfigDialog = async (pipelineName: string) => {
    setConfigDialog({ open: true, pipelineName, pipeline: null, loading: true, view: 'graph' });
    try {
      const resp = await kubernetesApi.proxy({
        clusterName: tektonCluster,
        path: `/apis/tekton.dev/v1/namespaces/${tektonNamespace}/pipelines/${pipelineName}`,
      });
      if (resp.ok) {
        const data = await resp.json();
        setConfigDialog(prev => ({ ...prev, pipeline: data, loading: false }));
      } else {
        setConfigDialog(prev => ({ ...prev, loading: false }));
      }
    } catch {
      setConfigDialog(prev => ({ ...prev, loading: false }));
    }
  };

  // 按 Tekton step 容器（step-*）拉取日志并拼接，得到干净、无 [step] 前缀的完整日志
  const loadPodLog = useCallback(async (podName: string): Promise<string> => {
    // 1. 取 Pod，找出业务 step 容器（排除 prepare/place-scripts 等 init 容器）
    let stepContainers: string[] = [];
    try {
      const podResp = await kubernetesApi.proxy({
        clusterName: tektonCluster,
        path: `/api/v1/namespaces/${tektonNamespace}/pods/${podName}`,
      });
      if (podResp.ok) {
        const pod = await podResp.json();
        stepContainers = (pod.spec?.containers || [])
          .map((c: any) => c.name)
          .filter((n: string) => n.startsWith('step-'));
      }
    } catch { /* ignore，下面有兜底 */ }

    if (stepContainers.length === 0) stepContainers = ['step-main'];

    // 2. 逐个 step 容器取日志，多 step 用分隔线区分
    const parts: string[] = [];
    for (const c of stepContainers) {
      try {
        const r = await kubernetesApi.proxy({
          clusterName: tektonCluster,
          path: `/api/v1/namespaces/${tektonNamespace}/pods/${podName}/log?container=${c}&tailLines=1000`,
        });
        if (r.ok) {
          const t = await r.text();
          if (t.trim()) {
            parts.push(
              stepContainers.length > 1
                ? `──────── ${c.replace(/^step-/, '')} ────────\n${t.trimEnd()}`
                : t.trimEnd(),
            );
          }
        }
      } catch { /* 跳过取不到的容器 */ }
    }
    return parts.join('\n\n') || '（空日志）';
  }, [kubernetesApi, tektonCluster, tektonNamespace]);

  // 日志自动刷新：弹窗打开且 Task 还在 Running 时每 5s 重拉
  useEffect(() => {
    if (!logDialog.open || !logDialog.podName) return undefined;
    if (!['Running', 'Pending'].includes(logDialog.taskStatus)) return undefined;

    const timer = setInterval(async () => {
      const log = await loadPodLog(logDialog.podName);
      setLogDialog(prev => (prev.open ? { ...prev, log } : prev));
    }, 5000);
    return () => clearInterval(timer);
  }, [logDialog.open, logDialog.podName, logDialog.taskStatus, loadPodLog]);

  const toggleExpand = async (runName: string) => {
    setExpandedRuns(prev => {
      const next = new Set(prev);
      if (next.has(runName)) next.delete(runName);
      else next.add(runName);
      return next;
    });
    if (!runDetails[runName]) {
      setDetailsLoading(prev => ({ ...prev, [runName]: true }));
      try {
        const resp = await kubernetesApi.proxy({
          clusterName: tektonCluster,
          path: `/apis/tekton.dev/v1/namespaces/${tektonNamespace}/pipelineruns/${runName}`,
        });
        if (resp.ok) {
          const data = await resp.json();
          if (data.status?.childReferences?.length) {
            const taskRunsMap = data.status.taskRuns || {};
            const needsFetch = data.status.childReferences.some(
              (ref: any) => !taskRunsMap[ref.name]?.status?.conditions?.length,
            );
            if (needsFetch) {
              const fetchedTaskRuns: Record<string, any> = {};
              await Promise.all(
                data.status.childReferences.map(async (ref: any) => {
                  try {
                    const trResp = await kubernetesApi.proxy({
                      clusterName: tektonCluster,
                      path: `/apis/tekton.dev/v1/namespaces/${tektonNamespace}/taskruns/${ref.name}`,
                    });
                    if (trResp.ok) fetchedTaskRuns[ref.name] = await trResp.json();
                  } catch { /* ignore */ }
                }),
              );
              data._fetchedTaskRuns = fetchedTaskRuns;
            }
          }
          setRunDetails(prev => ({ ...prev, [runName]: data }));
        }
      } catch (e) {
        console.warn('Failed to fetch run details', e);
      } finally {
        setDetailsLoading(prev => ({ ...prev, [runName]: false }));
      }
    }
  };

  const getTaskStages = (detail: any): Array<{ name: string; status: string; duration: number; podName?: string; startTime?: string }> => {
    if (!detail?.status?.childReferences && !detail?.status?.taskRuns) return [];
    const stages: Array<{ name: string; status: string; duration: number; podName?: string; startTime?: string }> = [];

    if (detail.status.childReferences) {
      for (const ref of detail.status.childReferences) {
        const trStatus = detail.status.taskRuns?.[ref.name] || detail._fetchedTaskRuns?.[ref.name] || {};
        const conditions = trStatus.status?.conditions || [];
        const succeeded = conditions.find((c: any) => c.type === 'Succeeded');
        let taskStatus = 'Pending';
        if (succeeded) {
          if (succeeded.status === 'True') taskStatus = 'Succeeded';
          else if (succeeded.status === 'False') taskStatus = 'Failed';
          else taskStatus = 'Running';
        } else if (trStatus.status?.startTime) {
          taskStatus = 'Running';
        }
        const dur = durationSeconds(trStatus.status?.startTime, trStatus.status?.completionTime);
        stages.push({
          name: ref.pipelineTaskName || ref.name,
          status: taskStatus,
          duration: dur,
          podName: trStatus.status?.podName,
          startTime: trStatus.status?.startTime,
        });
      }
    }

    if (stages.length === 0 && detail.status.taskRuns) {
      for (const [trName, trData] of Object.entries<any>(detail.status.taskRuns)) {
        const conditions = trData.status?.conditions || [];
        const succeeded = conditions.find((c: any) => c.type === 'Succeeded');
        let taskStatus = 'Pending';
        if (succeeded) {
          if (succeeded.status === 'True') taskStatus = 'Succeeded';
          else if (succeeded.status === 'False') taskStatus = 'Failed';
          else taskStatus = 'Running';
        } else if (trData.status?.startTime) {
          taskStatus = 'Running';
        }
        const dur = durationSeconds(trData.status?.startTime, trData.status?.completionTime);
        stages.push({
          name: trData.pipelineTaskName || trName,
          status: taskStatus,
          duration: dur,
          podName: trData.status?.podName,
          startTime: trData.status?.startTime,
        });
      }
    }

    stages.sort((a, b) => {
      if (!a.startTime && !b.startTime) return 0;
      if (!a.startTime) return 1;
      if (!b.startTime) return -1;
      return new Date(a.startTime).getTime() - new Date(b.startTime).getTime();
    });
    return stages;
  };

  const fetchTaskLog = async (podName: string, taskName: string, taskStatus: string = '') => {
    setLogDialog({ open: true, taskName, log: '', loading: true, podName, taskStatus });
    try {
      const log = await loadPodLog(podName);
      setLogDialog(prev => ({ ...prev, log, loading: false }));
    } catch (err) {
      setLogDialog(prev => ({ ...prev, log: `获取日志失败: ${String(err)}`, loading: false }));
    }
  };

  // ── 每个 Pipeline 的统计（与 k8s pipelineStats 一致，数据源换成 knative runs） ──
  const pipelineStats = useMemo(() => {
    const stats: Record<string, {
      runs: TektonPipelineRun[];
      succeeded: number;
      failed: number;
      running: number;
      avgDuration: number;
      successRate: number;
      lastRun?: TektonPipelineRun;
      overallStatus: 'ok' | 'warn' | 'error' | 'idle';
    }> = {};

    PIPELINE_ORDER.forEach(name => {
      const runs = (pipelineRuns as TektonPipelineRun[]).filter(r => r.spec?.pipelineRef?.name === name);
      const completed = runs.filter(r => ['Succeeded', 'Failed'].includes(getRunStatus(r)));
      const succeeded = runs.filter(r => getRunStatus(r) === 'Succeeded').length;
      const failed = runs.filter(r => getRunStatus(r) === 'Failed').length;
      const running = runs.filter(r => ['Running', 'Pending'].includes(getRunStatus(r))).length;

      const durations = completed
        .filter(r => r.status?.startTime && r.status?.completionTime)
        .map(r => durationSeconds(r.status!.startTime!, r.status!.completionTime!));
      const avgDuration = durations.length > 0 ? Math.round(durations.reduce((a, b) => a + b, 0) / durations.length) : 0;

      const total = succeeded + failed;
      const successRate = total > 0 ? Math.round((succeeded / total) * 100) : 0;

      let overallStatus: 'ok' | 'warn' | 'error' | 'idle' = 'idle';
      if (runs.length > 0) {
        const last = getRunStatus(runs[0]);
        if (last === 'Succeeded') overallStatus = 'ok';
        else if (last === 'Failed') overallStatus = 'error';
        else if (last === 'Running' || last === 'Pending') overallStatus = 'warn';
        else overallStatus = successRate >= 80 ? 'ok' : 'warn';
      }

      stats[name] = { runs, succeeded, failed, running, avgDuration, successRate, lastRun: runs[0], overallStatus };
    });

    return stats;
  }, [pipelineRuns]);

  const triggerExecute = useCallback((pipelineName: string) => {
    if (pipelineName.includes('traffic')) openTrafficDialog();
    else openReleaseDialog();
  }, [openReleaseDialog, openTrafficDialog]);

  // ── Render ──

  if (!hasKnativeConfig) {
    return (
      <Alert severity="info">
        当前组件缺少 Knative 注解（knative.dev/service-name 等），无法展示 CD Pipelines。
      </Alert>
    );
  }

  return (
    <div className={classes.root}>
      {/* ── 顶部栏 ── */}
      <Box className={classes.filterBar}>
        <Typography variant="body2" color="textSecondary" style={{ lineHeight: '28px' }}>
          管控集群：{tektonCluster} / {tektonNamespace}
        </Typography>
        <Box style={{ marginLeft: 'auto' }}>
          <Button size="small" startIcon={<RefreshIcon />} onClick={() => refresh()} disabled={loading} style={{ textTransform: 'none' }}>
            刷新
          </Button>
        </Box>
      </Box>

      {loading && (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress size={24} />
        </Box>
      )}

      {/* ── Pipeline 卡片列表 ── */}
      <Box style={{ border: '1px solid', borderColor: 'rgba(0,0,0,0.08)', borderRadius: 8 }}>
        {PIPELINE_ORDER.map(pipelineName => {
          const pmeta = PIPELINE_META[pipelineName];
          const stats = pipelineStats[pipelineName];
          if (!pmeta || !stats) return null;

          const recentRuns = stats.runs.slice(0, 20).reverse();
          // 含刚触发的静默期：点完立刻显示"运行中"，不必等 Results 轮询
          const hasRunning = stats.running > 0 || executingPipelines.has(pipelineName);

          return (
            <Box key={pipelineName} className={classes.pipelineCard}>
              <Box className={classes.pipelineInfo}>
                <Box display="flex" alignItems="center" style={{ gap: 6, marginBottom: 4 }}>
                  {hasRunning ? (
                    <CircularProgress size={16} style={{ marginTop: -4 }} />
                  ) : (
                    <>
                      {stats.overallStatus === 'ok' && <CheckCircleIcon style={{ color: '#4caf50', fontSize: 20, marginTop: -7 }} />}
                      {stats.overallStatus === 'error' && <CancelIcon style={{ color: '#f44336', fontSize: 20, marginTop: -7 }} />}
                      {stats.overallStatus === 'warn' && <AccessTimeIcon style={{ color: '#ff9800', fontSize: 20, marginTop: -7 }} />}
                      {stats.overallStatus === 'idle' && <RadioButtonUncheckedIcon style={{ color: '#bdbdbd', fontSize: 20, marginTop: -7 }} />}
                    </>
                  )}
                  {pmeta.tags.map(tag => (
                    <Chip key={tag} label={tag} size="small" className={classes.tag}
                      variant="outlined" style={{ color: '#1976d2', borderColor: '#1976d233' }} />
                  ))}
                </Box>
                <Tooltip title={pmeta.description || ''}>
                  <Typography
                    className={classes.pipelineName}
                    onClick={() => setHistoryDialog({ open: true, pipelineName })}
                  >
                    {displayName(pipelineName)}
                  </Typography>
                </Tooltip>
              </Box>

              {/* Mini 柱状图 */}
              <Box className={classes.miniChart}>
                {recentRuns.length === 0 ? (
                  <Typography variant="caption" color="textSecondary" style={{ alignSelf: 'center' }}>暂无运行</Typography>
                ) : (
                  recentRuns.map((run, idx) => {
                    const status = getRunStatus(run);
                    const d = durationSeconds(run.status?.startTime, run.status?.completionTime);
                    const maxD = Math.max(...recentRuns.map(r => durationSeconds(r.status?.startTime, r.status?.completionTime)), 1);
                    const height = Math.max(6, Math.round((d / maxD) * 36));
                    return (
                      <Tooltip key={idx} title={`${run.metadata.name} — ${status} — ${formatDuration(d)}`}>
                        <div
                          className={classes.miniBar}
                          style={{
                            height,
                            backgroundColor: getStatusColor(status),
                            opacity: status === 'Running' ? 0.5 : 0.85,
                          }}
                        />
                      </Tooltip>
                    );
                  })
                )}
              </Box>

              {/* 统计指标 */}
              <Box className={classes.statsSection}>
                <Box className={classes.statItem}>
                  <Typography className={classes.statValue}>
                    {stats.avgDuration > 0 ? formatDuration(stats.avgDuration) : '-'}
                  </Typography>
                  <Typography className={classes.statLabel}>平均执行时间</Typography>
                </Box>

                <Box className={classes.statItem}>
                  <Typography className={classes.statValue}
                    style={{ color: stats.successRate >= 80 ? '#4caf50' : stats.successRate >= 50 ? '#ff9800' : stats.runs.length > 0 ? '#f44336' : 'inherit' }}>
                    {stats.runs.length > 0 ? `${stats.successRate}%` : '-'}
                  </Typography>
                  <Typography className={classes.statLabel}>成功率</Typography>
                </Box>

                <Box className={classes.statItem}>
                  <Typography className={classes.statValue} style={{ fontSize: '0.8rem' }}>
                    {stats.lastRun ? formatDate(stats.lastRun.status?.startTime || stats.lastRun.metadata.creationTimestamp) : '-'}
                  </Typography>
                  <Typography className={classes.statLabel}>
                    最近执行{stats.lastRun && getTriggeredBy(stats.lastRun) ? ` · by ${getTriggeredBy(stats.lastRun).split('@')[0]}` : ''}
                  </Typography>
                </Box>
              </Box>

              {/* 执行按钮 */}
              <Button
                className={classes.executeBtn}
                variant="contained"
                color="primary"
                size="small"
                startIcon={hasRunning ? <CircularProgress size={14} style={{ color: '#fff' }} /> : <PlayArrowIcon />}
                onClick={() => triggerExecute(pipelineName)}
                disabled={hasRunning}
                style={{ textTransform: 'none', minWidth: 80, borderRadius: 6 }}
              >
                {hasRunning ? '运行中' : '执行'}
              </Button>
              <Tooltip title="查看流水线配置">
                <IconButton
                  size="small"
                  onClick={() => openConfigDialog(pipelineName)}
                  style={{ marginLeft: 4, color: 'inherit', opacity: 0.6 }}
                >
                  <SettingsIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          );
        })}
      </Box>

      {/* ── 历史记录 Dialog ── */}
      <Dialog
        open={historyDialog.open}
        onClose={() => setHistoryDialog({ open: false, pipelineName: '' })}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle disableTypography className={classes.historyDialogTitle}>
          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            <span>{PIPELINE_META[historyDialog.pipelineName]?.icon}</span>
            <Typography variant="h6" style={{ fontWeight: 600 }}>
              {displayName(historyDialog.pipelineName)} — 历史任务
            </Typography>
            <Chip size="small" label={`共 ${(pipelineStats[historyDialog.pipelineName]?.runs || []).length} 次`}
              variant="outlined" style={{ marginLeft: 8 }} />
          </Box>
          <Box display="flex" alignItems="center" style={{ gap: 4 }}>
            <Button size="small" startIcon={<RefreshIcon />} onClick={() => refresh()} disabled={loading} style={{ textTransform: 'none' }}>
              刷新
            </Button>
            <IconButton size="small" onClick={() => setHistoryDialog({ open: false, pipelineName: '' })}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent style={{ padding: 0 }}>
          {(() => {
            const runs = pipelineStats[historyDialog.pipelineName]?.runs || [];
            if (runs.length === 0) {
              return (
                <Box p={4} textAlign="center">
                  <Typography variant="body2" color="textSecondary">暂无运行记录</Typography>
                </Box>
              );
            }
            return (
              <Table className={classes.historyTable} size="small">
                <TableHead>
                  <TableRow>
                    <TableCell style={{ width: 40 }} />
                    <TableCell>ID</TableCell>
                    <TableCell>运行状态</TableCell>
                    <TableCell>持续时间</TableCell>
                    <TableCell>目标集群</TableCell>
                    <TableCell>命名空间</TableCell>
                    <TableCell>Service</TableCell>
                    <TableCell>执行人</TableCell>
                    <TableCell>执行时间</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {runs.map((run, idx) => {
                    const status = getRunStatus(run);
                    const dur = durationSeconds(run.status?.startTime, run.status?.completionTime);
                    const target = getRunTarget(run);
                    const params = run.spec?.params || [];
                    const svcParam = params.find(p => p.name === 'service-name');
                    const targetName = (typeof svcParam?.value === 'string' ? svcParam.value : '') || '-';
                    const triggeredBy = getTriggeredBy(run) || '-';

                    const isExpanded = expandedRuns.has(run.metadata.name);
                    const stages = isExpanded ? getTaskStages(runDetails[run.metadata.name]) : [];
                    const isLoadingDetail = detailsLoading[run.metadata.name];
                    const errorMessage = getRunErrorMessage(run);

                    return (
                      <React.Fragment key={run.metadata.name}>
                        <TableRow hover style={{ cursor: 'pointer' }} onClick={() => toggleExpand(run.metadata.name)}>
                          <TableCell>
                            {isExpanded
                              ? <ExpandMoreIcon className={classes.expandIcon} />
                              : <ChevronRightIcon className={classes.expandIcon} />}
                          </TableCell>
                          <TableCell>
                            <Typography className={classes.historyRunId}>
                              #{runs.length - idx}
                            </Typography>
                            <Typography variant="caption" color="textSecondary" style={{ fontFamily: 'monospace', fontSize: '0.65rem', display: 'block', marginTop: 2 }}>
                              {run.metadata.name}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={status === 'Succeeded' ? '成功' : status === 'Failed' ? '失败' : status === 'Running' ? '运行中' : status === 'Cancelled' ? '已取消' : '等待中'}
                              className={classes.historyStatusChip}
                              style={{ backgroundColor: `${getStatusColor(status)}18`, color: getStatusColor(status) }}
                            />
                          </TableCell>
                          <TableCell>
                            <Box display="flex" alignItems="center" style={{ gap: 4 }}>
                              <span style={{ fontSize: 14, color: '#999' }}>⏱</span>
                              {status === 'Running' || status === 'Pending'
                                ? formatDuration(durationSeconds(run.status?.startTime, undefined))
                                : formatDuration(dur)}
                            </Box>
                          </TableCell>
                          <TableCell>{target.cluster || '-'}</TableCell>
                          <TableCell>{target.namespace || '-'}</TableCell>
                          <TableCell>
                            <Typography variant="body2" style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                              {targetName}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" style={{ fontSize: '0.8rem' }}>
                              {triggeredBy.split('@')[0]}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" style={{ fontSize: '0.8rem' }}>
                              {formatDate(run.status?.startTime || run.metadata.creationTimestamp)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                        {isExpanded && (
                          <TableRow className={classes.expandedRow}>
                            <TableCell colSpan={9} style={{ padding: 0 }}>
                              {isLoadingDetail ? (
                                <Box display="flex" alignItems="center" justifyContent="center" p={2} style={{ gap: 8 }}>
                                  <CircularProgress size={16} />
                                  <Typography variant="body2" color="textSecondary">加载 Stage 详情...</Typography>
                                </Box>
                              ) : stages.length === 0 ? (
                                <Box p={2}>
                                  <Typography variant="body2" color="textSecondary">暂无 Stage 信息</Typography>
                                  {errorMessage && (
                                    <Box mt={1} p={1} style={{ backgroundColor: '#fff0f0', borderRadius: 4, border: '1px solid #ffccd5' }}>
                                      <Typography variant="caption" color="error" style={{ fontFamily: 'monospace' }}>
                                        <strong>⚠️ 核心故障原因:</strong><br />
                                        {errorMessage}
                                      </Typography>
                                    </Box>
                                  )}
                                </Box>
                              ) : (
                                <Box style={{ padding: '4px 0 4px 48px' }}>
                                  {stages.map((stage, sIdx) => (
                                    <Box key={sIdx} className={classes.stageRow}>
                                      <Typography className={classes.stageName}>
                                        {stage.name}
                                      </Typography>
                                      <Chip
                                        label={stage.status === 'Succeeded' ? '成功' : stage.status === 'Failed' ? '失败' : stage.status === 'Running' ? '运行中' : '等待中'}
                                        className={classes.stageStatusChip}
                                        style={{ backgroundColor: `${getStatusColor(stage.status)}18`, color: getStatusColor(stage.status) }}
                                      />
                                      <Box className={classes.stageDuration}>
                                        <span>⏱</span>
                                        {formatDuration(stage.duration)}
                                      </Box>
                                      {stage.podName && (
                                        <Button
                                          className={classes.logButton}
                                          size="small"
                                          variant="outlined"
                                          startIcon={<DescriptionIcon style={{ fontSize: 14 }} />}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            fetchTaskLog(stage.podName!, stage.name, stage.status);
                                          }}
                                        >
                                          日志
                                        </Button>
                                      )}
                                    </Box>
                                  ))}
                                </Box>
                              )}
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* ── 日志查看 Dialog ── */}
      <Dialog
        open={logDialog.open}
        onClose={() => setLogDialog({ open: false, taskName: '', log: '', loading: false, podName: '', taskStatus: '' })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle disableTypography style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            <DescriptionIcon color="primary" />
            <Typography variant="h6" style={{ fontWeight: 600 }}>
              {logDialog.taskName} — 日志
            </Typography>
            {['Running', 'Pending'].includes(logDialog.taskStatus) && (
              <Box display="flex" alignItems="center" style={{ gap: 4, marginLeft: 8 }}>
                <CircularProgress size={12} />
                <Typography variant="caption" color="textSecondary">5s 自动刷新</Typography>
              </Box>
            )}
          </Box>
          <IconButton size="small" onClick={() => setLogDialog({ open: false, taskName: '', log: '', loading: false, podName: '', taskStatus: '' })}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent style={{ padding: 0 }}>
          {logDialog.loading ? (
            <Box display="flex" alignItems="center" justifyContent="center" p={4} style={{ gap: 8 }}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="textSecondary">加载日志中...</Typography>
            </Box>
          ) : (
            <pre style={{
              margin: 0,
              padding: 20,
              backgroundColor: '#0d1117',
              color: '#c9d1d9',
              fontFamily: '"JetBrains Mono", "Fira Code", "Cascadia Code", Consolas, "Courier New", monospace',
              fontSize: '0.82rem',
              lineHeight: 1.7,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
              maxHeight: '65vh',
              overflow: 'auto',
            }}>
              {logDialog.log}
            </pre>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Pipeline 配置查看 Dialog ── */}
      <Dialog
        open={configDialog.open}
        onClose={() => setConfigDialog(prev => ({ ...prev, open: false }))}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle disableTypography style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 20px' }}>
          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            <AccountTreeIcon color="primary" fontSize="small" />
            <Typography variant="h6" style={{ fontWeight: 600, fontSize: '1rem' }}>
              {displayName(configDialog.pipelineName)} — 流水线配置
            </Typography>
            <Chip label="只读" size="small" style={{ fontSize: '0.65rem', height: 18, opacity: 0.6 }} />
          </Box>
          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            <div className={classes.viewToggle}>
              <button
                className={classes.viewToggleBtn}
                onClick={() => setConfigDialog(prev => ({ ...prev, view: 'graph' }))}
                style={{
                  backgroundColor: configDialog.view === 'graph' ? 'rgba(25,118,210,0.12)' : 'transparent',
                  color: configDialog.view === 'graph' ? '#1976d2' : undefined,
                }}
              >
                <AccountTreeIcon style={{ fontSize: 14 }} />
                流程图
              </button>
              <button
                className={classes.viewToggleBtn}
                onClick={() => setConfigDialog(prev => ({ ...prev, view: 'yaml' }))}
                style={{
                  backgroundColor: configDialog.view === 'yaml' ? 'rgba(25,118,210,0.12)' : 'transparent',
                  color: configDialog.view === 'yaml' ? '#1976d2' : undefined,
                  borderLeft: '1px solid rgba(0,0,0,0.12)',
                }}
              >
                <CodeIcon style={{ fontSize: 14 }} />
                YAML
              </button>
            </div>
            <IconButton size="small" onClick={() => setConfigDialog(prev => ({ ...prev, open: false }))}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent style={{ padding: 0, display: 'flex', flexDirection: 'column', height: '72vh' }}>
          {configDialog.loading ? (
            <Box display="flex" alignItems="center" justifyContent="center" flex={1}>
              <CircularProgress size={28} />
            </Box>
          ) : configDialog.view === 'yaml' ? (
            <pre style={{
              margin: 0,
              padding: 24,
              backgroundColor: '#0d1117',
              color: '#c9d1d9',
              fontFamily: '"JetBrains Mono", Consolas, monospace',
              fontSize: '0.8rem',
              lineHeight: 1.7,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
              flex: 1,
              overflow: 'auto',
            }}>
              {configDialog.pipeline
                ? JSON.stringify(configDialog.pipeline, null, 2)
                  .replace(/"([^"]+)":/g, '$1:')
                  .replace(/"([^"]*)"/g, "'$1'")
                : '无法加载 Pipeline 配置'}
            </pre>
          ) : (
            <Box className={classes.configBody}>
              <Box className={classes.configCanvas}>
                {configDialog.pipeline ? (() => {
                  const tasks: any[] = configDialog.pipeline?.spec?.tasks || [];

                  const buildStages = (allTasks: any[]): any[][] => {
                    const stages: any[][] = [];
                    const assigned = new Set<string>();
                    const stage0 = allTasks.filter(t => !t.runAfter || t.runAfter.length === 0);
                    if (stage0.length > 0) { stages.push(stage0); stage0.forEach(t => assigned.add(t.name)); }
                    let remaining = allTasks.filter(t => !assigned.has(t.name));
                    while (remaining.length > 0) {
                      const next = remaining.filter(t => (t.runAfter || []).every((d: string) => assigned.has(d)));
                      if (next.length === 0) break;
                      stages.push(next);
                      next.forEach(t => assigned.add(t.name));
                      remaining = remaining.filter(t => !assigned.has(t.name));
                    }
                    return stages;
                  };
                  const stages = buildStages(tasks);

                  const Connector = ({ addStage }: { addStage?: boolean }) => (
                    <Box display="flex" alignItems="center" style={{ gap: 0 }}>
                      <Box className={classes.flowDot} />
                      <Box className={classes.flowLine} style={{ width: addStage ? 20 : 28 }} />
                      {addStage && (
                        <>
                          <span className={classes.flowAddStageBtn}>+ 阶段</span>
                          <Box className={classes.flowLine} style={{ width: 20 }} />
                        </>
                      )}
                    </Box>
                  );

                  return (
                    <Box className={classes.flowContainer}>
                      <Typography className={classes.flowStartEndLabel}>Start</Typography>
                      <Connector />

                      {stages.map((stageTasks, stageIdx) => (
                        <React.Fragment key={stageIdx}>
                          <Box className={classes.flowStageColumn}>
                            <Box className={classes.flowStageHeader}>
                              <Typography className={classes.flowStageName}>
                                {stageTasks.length === 1 ? stageTasks[0].name : `stage-${stageIdx + 1}`}
                              </Typography>
                              <Tooltip title="只读，不可编辑">
                                <SettingsIcon className={classes.flowStageGear} />
                              </Tooltip>
                            </Box>
                            <Box className={classes.flowStageBox}>
                              {stageTasks.map((task: any) => (
                                <Box
                                  key={task.name}
                                  className={classes.flowTaskBox}
                                  onClick={e => setTaskPopover({ anchorEl: e.currentTarget as HTMLElement, task })}
                                  style={{ cursor: 'pointer' }}
                                  title="点击查看详情"
                                >
                                  <Typography className={classes.flowTaskName}>{task.name}</Typography>
                                </Box>
                              ))}
                              <Box className={classes.flowAddTaskBtn}>+ 任务</Box>
                            </Box>
                          </Box>

                          {stageIdx < stages.length - 1 ? <Connector /> : <Connector addStage />}
                        </React.Fragment>
                      ))}

                      {tasks.length === 0 && <Connector addStage />}

                      <Typography className={classes.flowStartEndLabel}>End</Typography>
                    </Box>
                  );
                })() : (
                  <Typography color="textSecondary">无法加载 Pipeline 配置</Typography>
                )}
              </Box>

              <Box className={classes.configSidebar}>
                <Box className={classes.sidebarSection}>
                  <Typography className={classes.sidebarLabel}>参数</Typography>
                  {(configDialog.pipeline?.spec?.params || []).length === 0 ? (
                    <Typography style={{ fontSize: '0.75rem', color: '#999' }}>无</Typography>
                  ) : (
                    (configDialog.pipeline?.spec?.params || []).map((p: any, i: number) => (
                      <Box key={i} className={classes.sidebarItem}>
                        <Typography style={{ fontSize: '0.78rem', fontWeight: 600 }}>{p.name}</Typography>
                        {p.description && (
                          <Typography style={{ fontSize: '0.7rem', color: '#999' }}>{p.description}</Typography>
                        )}
                        {p.default !== undefined && (
                          <Typography style={{ fontSize: '0.7rem', color: '#aaa', fontFamily: 'monospace' }}>默认: {p.default}</Typography>
                        )}
                      </Box>
                    ))
                  )}
                </Box>

                {(configDialog.pipeline?.spec?.workspaces || []).length > 0 && (
                  <Box className={classes.sidebarSection}>
                    <Typography className={classes.sidebarLabel}>工作空间</Typography>
                    {(configDialog.pipeline?.spec?.workspaces || []).map((w: any, i: number) => (
                      <Box key={i} className={classes.sidebarItem}>
                        <Typography style={{ fontSize: '0.78rem', fontWeight: 600 }}>{w.name}</Typography>
                        {w.description && (
                          <Typography style={{ fontSize: '0.7rem', color: '#999' }}>{w.description}</Typography>
                        )}
                      </Box>
                    ))}
                  </Box>
                )}

                {configDialog.pipeline?.spec?.description && (
                  <Box className={classes.sidebarSection}>
                    <Typography className={classes.sidebarLabel}>描述</Typography>
                    <Typography style={{ fontSize: '0.75rem', color: '#888', lineHeight: 1.5 }}>
                      {configDialog.pipeline.spec.description}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Task 详情 Popover */}
      <Popover
        open={Boolean(taskPopover.anchorEl)}
        anchorEl={taskPopover.anchorEl}
        onClose={() => setTaskPopover({ anchorEl: null, task: null })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        PaperProps={{ style: { maxWidth: 380, borderRadius: 8, boxShadow: '0 4px 24px rgba(0,0,0,0.14)' } }}
      >
        {taskPopover.task && (() => {
          const t = taskPopover.task;
          const steps: any[] = t.taskSpec?.steps || [];
          const params: any[] = t.params || [];
          const workspaces: any[] = t.workspaces || [];
          return (
            <Box style={{ padding: '16px 20px', minWidth: 280 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" style={{ marginBottom: 12 }}>
                <Typography style={{ fontWeight: 700, fontSize: '0.9rem' }}>{t.name}</Typography>
                {t.runAfter && t.runAfter.length > 0 && (
                  <Typography style={{ fontSize: '0.7rem', color: '#888', background: '#f0f4ff', padding: '2px 8px', borderRadius: 10 }}>
                    After: {t.runAfter.join(', ')}
                  </Typography>
                )}
              </Box>

              {params.length > 0 && (
                <Box style={{ marginBottom: 12 }}>
                  <Typography style={{ fontSize: '0.72rem', fontWeight: 700, color: '#1976d2', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    参数
                  </Typography>
                  {params.map((p: any, i: number) => (
                    <Box key={i} display="flex" justifyContent="space-between" style={{ fontSize: '0.75rem', padding: '3px 0', borderBottom: '1px solid #f0f0f0' }}>
                      <Typography style={{ fontSize: '0.75rem', color: '#555', fontWeight: 500 }}>{p.name}</Typography>
                      <Typography style={{ fontSize: '0.72rem', color: '#888', maxWidth: 200, textAlign: 'right', wordBreak: 'break-all' }}>
                        {String(p.value || '').replace(/\$\(params\./g, '').replace(/\)/g, '')}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}

              {steps.length > 0 && (
                <Box style={{ marginBottom: 12 }}>
                  <Typography style={{ fontSize: '0.72rem', fontWeight: 700, color: '#1976d2', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Steps ({steps.length})
                  </Typography>
                  {steps.map((s: any, i: number) => (
                    <Box key={i} style={{ padding: '4px 0', borderBottom: '1px solid #f0f0f0' }}>
                      <Typography style={{ fontSize: '0.75rem', fontWeight: 500 }}>{s.name || `step-${i + 1}`}</Typography>
                      <Typography style={{ fontSize: '0.68rem', color: '#aaa', wordBreak: 'break-all' }}>{s.image}</Typography>
                    </Box>
                  ))}
                </Box>
              )}

              {workspaces.length > 0 && (
                <Box>
                  <Typography style={{ fontSize: '0.72rem', fontWeight: 700, color: '#1976d2', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Workspaces
                  </Typography>
                  {workspaces.map((w: any, i: number) => (
                    <Box key={i} display="flex" justifyContent="space-between" style={{ fontSize: '0.75rem', padding: '3px 0' }}>
                      <Typography style={{ fontSize: '0.75rem', color: '#555' }}>{w.name}</Typography>
                      <Typography style={{ fontSize: '0.72rem', color: '#888' }}>→ {w.workspace}</Typography>
                    </Box>
                  ))}
                </Box>
              )}

              {params.length === 0 && steps.length === 0 && workspaces.length === 0 && (
                <Typography style={{ fontSize: '0.75rem', color: '#aaa' }}>暂无详细信息</Typography>
              )}
            </Box>
          );
        })()}
      </Popover>

      {/* 执行对话框（发版 / 切流，来自 useKnativePipelineActions） */}
      {dialogs}
    </div>
  );
};
